<template>
  <v-dialog v-model="dialog" class="accept-dialog" :max-width="isMobile ? '332px' : '614px'">
    <template v-slot:activator="{ on, attrs }">
      <div v-bind="attrs" v-on="on">
        <mf-button id="industry_proposal_accept_campaign_button" label="Aceitar campanha" color="verde-l10" icon="check" icon-position="left" />
      </div>
    </template>
    <v-progress-linear v-if="loading" class="py-1" rounded indeterminate color="verde-l10" />
    <v-card class="accept-card">
      <span class="accept-title">Confirmar aceite da campanha</span>
      <div class="accept-content">
        <span class="cinza-d10-text">Para garantir uma melhor experiência para você e seus clientes, sugerimos:</span>

        <div v-for="(tip, index) in acceptTips" :key="index" class="accept-tip">
          <mf-icon :icon="tip.icon" color="cinza-l30" />
          <span class="cinza-0-text">{{ tip.text }}</span>
        </div>

        <div v-if="downloadCampaignUrl" class="download-container">
          <div class="download-content">
            <mf-icon icon="contract" fill color="marca-0-l30" />
            <span class="marca-0-l10-text">Esta campanha possui um panfleto de divulgação na loja, você pode baixá-lo no botão ao lado.</span>
          </div>
          <mf-button
            id="industry_proposal_accept_click_download"
            label="Fazer download"
            color="primary"
            size="sm"
            outlined
            icon="download"
            @click="downloadFile"
          />
        </div>

        <v-checkbox v-model="termsConditionsAccepted" color="marca-0-l30" readonly>
          <template v-slot:label>
            <div>
              <span class="cinza-d10-text">Ao aceitar participar dessa campanha você está de concordo com os </span>
              <terms-conditions-dialog
                id="industry_proposal_accept_terms_conditions"
                text-mode
                :banner="campanha.industry.acceptance.image"
                :markdown-text="campanha.industry.acceptance.terms"
              />
            </div>
          </template>
        </v-checkbox>

        <div class="accept-buttons">
          <mf-button id="industry_proposal_cancel_acceptance_modal" label="Cancelar" color="cinza-d20" text :disabled="loading" @click="closeDialog" />
          <mf-button label="Aceitar campanha" color="verde-l10" :disabled="isPrimaryButtonDisabled" :loading="loading" @click="submit" />
        </div>
      </div>
    </v-card>
    <processing-dialog v-model="showProcessingDialog" :message="processingMessage" @go-back="goBack" />
  </v-dialog>
</template>

<script>
import Swal from 'sweetalert2'
import { DateTime } from 'luxon'
import { campaignHelpers } from '@/modules/campanhas/gestao-ofertas-v2/views/mixins/campaign-helpers'
import { tradeCampaignProcessing } from '@/modules/industria/ofertas-trade/views/mixins/trade-campaign-processing'

export default {
  name: 'AcceptOfferDialog',
  components: {
    TermsConditionsDialog: () => import('@/modules/industria/ofertas-trade/views/components/offers-management/TermsConditionsDialog.vue'),
    ProcessingDialog: () => import('@/modules/industria/ofertas-trade/views/components/offers-management/ProcessingDialog.vue'),
  },
  mixins: [campaignHelpers, tradeCampaignProcessing],
  props: {
    campanha: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    loading: false,
    dialog: false,
    termsConditionsAccepted: true,
    isNewOffer: true,
    showProcessingDialog: false,
  }),
  computed: {
    processingMessage() {
      return this.timerPartialValue === this.timerTotalValue ? 'Aceitando campanha...' : this.timerLabel
    },
    isPrimaryButtonDisabled() {
      return !this.termsConditionsAccepted
    },
    acceptTips() {
      return [
        {
          icon: 'inventory',
          text: 'Verificar o estoque dos produtos oferecidos nesta campanha.',
        },
        {
          icon: 'store',
          text: 'Informar o time dentro das lojas sobre as mecânicas e datas da ação.',
        },
        {
          icon: 'chat',
          text: 'Reforçar a comunicação das ofertas nos mais diversos canais  (Enxoval de loja, Mídias sociais, WhatsApp, SMS, Push...) aumentará o alcance da campanha.',
        },
      ]
    },
    proposalDeadlineExpired() {
      return DateTime.now() > DateTime.fromISO(this.campanha?.industry?.proposal_deadline).endOf('day')
    },
    downloadCampaignUrl() {
      return this.campanha?.industry?.flyer_url
    },
  },
  async mounted() {
    await this.subscribeToOfferSchedule()
  },
  methods: {
    downloadFile() {
      window.open(this.downloadCampaignUrl, '_blank')
    },
    isValidForAccept() {
      if (!this.termsConditionsAccepted) return false
      if (this.loading) return false
      if (this.proposalDeadlineExpired) {
        Swal.fire({
          text: 'Não é possível ativar essa campanha pois o prazo de adesão expirou',
          icon: 'warning',
          showConfirmButton: true,
        })
        return false
      }
      return true
    },
    async submit() {
      if (!this.isValidForAccept()) return

      this.loading = true
      const isDraft = false
      const campaignToSave = this.formatCampaignToSave(isDraft)

      await this.processCampaign(campaignToSave, 'Erro ao aceitar campanha')
    },
    closeDialog() {
      this.dialog = false
    },

    onSubscriptionStart() {
      this.loading = true
      this.showProcessingDialog = true
    },

    onSubscriptionSuccess() {
      this.loading = false
      this.showProcessingDialog = false
      this.showSuccessMessage('Campanha aceita com sucesso')
    },

    onSubscriptionError() {
      this.loading = false
      this.showProcessingDialog = false
      this.showErrorMessage('Erro ao aceitar campanha')
    },

    onClose() {
      this.loading = false
      this.showProcessingDialog = false
      this.closeDialog()
      this.$router.push({ name: 'ofertas-trade-propostas' })
    },

    goBack() {
      this.loading = false
      this.showProcessingDialog = false
      this.$router.push({ name: 'ofertas-trade-propostas' })
    },
  },
}
</script>

<style lang="scss" scoped>
.accept-dialog {
  height: 378px;

  @media screen and (max-width: $xs-breakpoint) {
    height: 492px;
  }
}

.accept-card {
  padding: 20px 18px;

  .accept-title {
    font-size: 22px;
    font-weight: bold;
    color: $cinza-d10;

    @media screen and (max-width: $xs-breakpoint) {
      font-size: 20px;
    }
  }

  .accept-content {
    margin-top: 20px;
    font-size: 14px;

    .download-container {
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-radius: 8px;
      padding: 12px 16px;
      background: #d2e9ff80;
    }

    .download-content {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 12px;
      width: 63%;
    }

    .accept-tip {
      display: flex;
      flex-direction: row;
      align-items: center;
      margin: 24px 0;
      gap: 8px;
    }

    .marca-0-l10-text {
      color: $marca-0-l10;
    }

    .cinza-d10-text {
      color: $cinza-d10;
    }

    .cinza-0-text {
      font-size: 14px;
      color: $cinza-0;
    }

    .accept-buttons {
      display: flex;
      flex-direction: row;
      justify-content: flex-end;
      gap: 16px;
    }
  }
}

::v-deep .v-dialog {
  border: 1px solid $verde-l10;
  border-radius: 8px;
  box-shadow: 0px 2px 20px 0px #0000002f;
}
</style>
